import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useErrorStore } from '../../store/models';
import { validate, SignUpValidations, validatePasswordValue } from '../../utils/validations';

const signUpErrorMessages = {
    firstName: {
        required: 'First name is required.',
        pattern: 'Only letters, spaces, dots (.), and hyphens (-) are allowed.',
        dangerous: 'Only letters, spaces, dots (.), and hyphens (-) are allowed.',
    },

    lastName: {
        required: 'Last name is required.',
        pattern: 'Only letters, spaces, dots (.), and hyphens (-) are allowed.',
        dangerous: 'Only letters, spaces, dots (.), and hyphens (-) are allowed.',
    },

    email: {
        required: 'Email is required.',
        minLength: 'Email mininum length is 5',
        maxLength: 'Email maximum length is 256',
        pattern: 'Email format is incorrect, please follow email format john.jacob@domain.com',
        dangerous: 'Email is invalid.',
    },

    password: {
        required: 'Password is required',
        minLength: 'Password mininum length is 8',
        maxLength: 'Password maximum length is 32',
        pattern: 'Password needs one or more uppercase letter, special character and number',
        spaceAlert: 'Password cannot start or end with spaces',
        nonEnglishAlert: 'Password can only contain English characters',
        dangerous: 'Password is invalid.',
    },

    phone: {
        required: 'Please enter your phone number',
        minLength: 'Phone number is too short',
        maxLength: 'Phone number is too long',
        pattern: 'Please enter a valid phone number',
        dangerous: 'Please enter a valid phone number',
    },

    publisherName: {
        required: 'Publisher name is required',
        pattern: 'Only letters, numbers, spaces, dots (.), @, and hyphens (-) are allowed.',
        dangerous: 'Only letters, numbers, spaces, dots (.), @, and hyphens (-) are allowed.',
    },

    industry: {
        required: 'Industry is required',
        dangerous: 'Industry is invalid.',
    },

    objective: {
        required: 'Objective is required',
        dangerous: 'Objective is invalid.',
    },

    timezone: {
        required: 'Timezone is required',
        dangerous: 'Timezone is invalid.',
    },

};

function useAuthErrorHook (props) {
    const {
        signUpInput,
    } = props;

    const errStore = useErrorStore();

    const [_signUpInput, setSignUpInput] = useState(signUpInput);

    const [signUpError, setSignUpError] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        publisherName: '',
        phone: '',
        industry: '',
        objective: '',
        timezone: '',
        nextDisabled: true,
        finishDisabled: true,
    });
    const [touched, setTouched] = useState({});

    function toggleNext (err) {
        if (!err.email && !err.password && !err.lastName) {
            err.nextDisabled = false;
        } else {
            err.nextDisabled = true;
            return;
        }

        if (isEmpty(_signUpInput.email) ||
            isEmpty(_signUpInput.password) ||
            isEmpty(_signUpInput.firstName) ||
            isEmpty(_signUpInput.lastName)) {
            err.nextDisabled = true;
        } else {
            err.nextDisabled = false;
        }
    }

    function validateByChange () {
        if (isEmpty(touched)) {
            return;
        }

        const err = {
            firstName: validateFirstName(),
            lastName: validateLastName(),
            email: validateEmail(),
            password: validatePassword(),
            phone: validatePhone(),
            publisherName: validatePublisherName(),
            industry: validateIndustry(),
            objective: validateObjective(),
            timezone: validateTimezone(),
        };

        toggleNext(err);

        if (
            err.publisherName ||
            err.phone ||
            err.industry ||
            err.timezone ||
            err.objective ||
            !_signUpInput.publisherName ||
            !_signUpInput.phoneNumber ||
            !_signUpInput.industry ||
            !_signUpInput.objective ||
            !_signUpInput.timezone
        ) {
            err.finishDisabled = true;
        } else {
            err.finishDisabled = false;
        }

        setSignUpError(err);
    }

    function validateFirstName () {
        if (!touched.firstName) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.firstName },
            messages: signUpErrorMessages.firstName,
            value: _signUpInput.firstName,
        });
    }

    function validateLastName () {
        if (!touched.lastName) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.lastName },
            messages: signUpErrorMessages.lastName,
            value: _signUpInput.lastName,
        });
    }

    function validateEmail () {
        if (!touched.email) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.email },
            messages: signUpErrorMessages.email,
            value: _signUpInput.email,
        });
    }

    function validatePassword () {
        if (!touched.password) {
            return '';
        }

        return validatePasswordValue({
            rules: { ...SignUpValidations.password },
            messages: signUpErrorMessages.password,
            value: _signUpInput.password,
        });
    }

    function validatePhone () {
        if (!touched.phoneNumber) {
            return '';
        }

        return validate({
            rules: { ...SignUpValidations.phone },
            messages: signUpErrorMessages.phone,
            value: _signUpInput.phoneNumber,
        });
    }

    function validatePublisherName () {
        if (!touched.publisherName) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.publisherName },
            messages: signUpErrorMessages.publisherName,
            value: _signUpInput.publisherName,
        });
    }

    function validateIndustry () {
        if (!touched.industry) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.industry },
            messages: signUpErrorMessages.industry,
            value: _signUpInput.industry,
        });
    }

    function validateObjective () {
        if (!touched.objective) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.objective },
            messages: signUpErrorMessages.objective,
            value: _signUpInput.objective,
        });
    }

    function validateTimezone () {
        if (!touched.timezone) {
            return '';
        }
        return validate({
            rules: { ...SignUpValidations.timezone },
            messages: signUpErrorMessages.timezone,
            value: _signUpInput.timezone,
        });
    }

    function touchInputs () {
        const t = { ...touched };
        for (const p in signUpInput) {
            if (signUpInput[p] && t[p] !== true) {
                t[p] = true;
            }
        }
        setTouched(t);
    }

    useEffect(() => {
        touchInputs();
        setSignUpInput(signUpInput);
    }, [signUpInput]);

    useEffect(() => {
        // console.log('Touched: ', touched);
        if (Object.entries(touched).length > 0) {
            validateByChange();
        }
    }, [_signUpInput]);

    useEffect(() => {
        if (errStore.authError.signUp) {
            setSignUpError({
                email: errStore.authError.signUp.message,
            });
        }
    }, [errStore.authError.signUp]);

    useEffect(() => {
        validateByChange();
    }, []);
    return {
        signUpError,
    };
}

export default useAuthErrorHook;
